

import {
  ANIMATION_PHASES,
  FRAME_RATIOS,
  HIGHLIGHT_ZONE,
  SLIDE_CONFIG,
  SPEC_ITEMS_CONFIG,
  EASING
} from './constants';

export const calculateAnimationState = (currentFrame, totalFrames) => {
  // Validate inputs
  if (!totalFrames || totalFrames <= 0) {
    return createHiddenState();
  }
  
  // Normalize currentFrame to handle any edge cases
  const normalizedFrame = Math.max(0, Math.min(currentFrame, totalFrames - 1));
  
  // Calculate frame boundaries for each phase
  const boundaries = calculateFrameBoundaries(totalFrames);
  
  // Determine current phase and calculate progress
  const phaseInfo = determinePhaseAndProgress(normalizedFrame, boundaries);
  
  // Calculate item states based on phase and progress
  const itemStates = calculateItemStates(phaseInfo.phase, phaseInfo.progress);
  
  // Calculate snake line progress
  const snakeProgress = calculateSnakeProgress(phaseInfo.phase, phaseInfo.progress);
  
  return {
    // Phase information
    phase: phaseInfo.phase,
    phaseProgress: phaseInfo.progress,
    
    // Calculated animation values
    itemStates,
    snakeProgress,
    
    // Convenience flags
    isVisible: phaseInfo.phase !== ANIMATION_PHASES.HIDDEN,
    isFullyVisible: phaseInfo.phase === ANIMATION_PHASES.VISIBLE,
    isEntering: phaseInfo.phase === ANIMATION_PHASES.ENTERING,
    isExiting: phaseInfo.phase === ANIMATION_PHASES.EXITING,
    
    // Debug info
    boundaries,
    currentFrame: normalizedFrame
  };
};

export const calculateFrameBoundaries = (totalFrames) => {
  // Calculate frame counts based on ratios
  const entryFrames = Math.round(totalFrames * FRAME_RATIOS.ENTRY_RATIO);
  const stayFrames = Math.round(totalFrames * FRAME_RATIOS.STAY_RATIO);
  const exitFrames = Math.round(totalFrames * FRAME_RATIOS.EXIT_RATIO);
  
  // Calculate padding (hidden frames at start and end)
  const totalActiveFrames = entryFrames + stayFrames + exitFrames;
  const remainingFrames = totalFrames - totalActiveFrames;
  const startPadding = Math.floor(remainingFrames / 2);
  
  // Calculate frame ranges
  const entryStart = startPadding;
  const entryEnd = entryStart + entryFrames - 1;
  
  const stayStart = entryEnd + 1;
  const stayEnd = stayStart + stayFrames - 1;
  
  const exitStart = stayEnd + 1;
  const exitEnd = exitStart + exitFrames - 1;
  
  return {
    // Frame counts
    entryFrames,
    stayFrames,
    exitFrames,
    totalActiveFrames,
    startPadding,
    
    // Entry phase boundaries
    entryStart,
    entryEnd,
    
    // Stay phase boundaries  
    stayStart,
    stayEnd,
    
    // Exit phase boundaries
    exitStart,
    exitEnd,
    
    // Total frames
    totalFrames
  };
};

/**
 * Determine current phase and progress within that phase
 * 
 * @param {number} currentFrame - Current frame number
 * @param {Object} boundaries - Frame boundaries from calculateFrameBoundaries
 * @returns {Object} Phase name and progress (0-1)
 */
const determinePhaseAndProgress = (currentFrame, boundaries) => {
  const {
    entryStart, entryEnd, entryFrames,
    stayStart, stayEnd, stayFrames,
    exitStart, exitEnd, exitFrames
  } = boundaries;
  
  // Before entry phase - hidden
  if (currentFrame < entryStart) {
    return { phase: ANIMATION_PHASES.HIDDEN, progress: 0 };
  }
  
  // Entry phase
  if (currentFrame >= entryStart && currentFrame <= entryEnd) {
   
    const progress = (currentFrame - entryStart + 1) / (entryFrames || 1);
    return { 
      phase: ANIMATION_PHASES.ENTERING, 
      progress: Math.min(1, Math.max(0, progress))
    };
  }
  
  // Stay phase
  if (currentFrame >= stayStart && currentFrame <= stayEnd) {
    const progress = (currentFrame - stayStart) / (stayFrames - 1 || 1);
    return { 
      phase: ANIMATION_PHASES.VISIBLE, 
      progress: Math.min(1, Math.max(0, progress))
    };
  }
  
  // Exit phase
  if (currentFrame >= exitStart && currentFrame <= exitEnd) {
    // Same rationale as entry: ensure exit is visible when exitFrames is small.
    const progress = (currentFrame - exitStart + 1) / (exitFrames || 1);
    return { 
      phase: ANIMATION_PHASES.EXITING, 
      progress: Math.min(1, Math.max(0, progress))
    };
  }
  
  // After exit phase - hidden
  return { phase: ANIMATION_PHASES.HIDDEN, progress: 0 };
};

const calculateItemStates = (phase, progress) => {
  return SPEC_ITEMS_CONFIG.map((config, index) => {
    return calculateSingleItemState(config, index, phase, progress);
  });
};

/**
 * Calculate state for a single spec item
 * 
 * @param {Object} config - Item configuration
 * @param {number} index - Item index
 * @param {string} phase - Current phase
 * @param {number} progress - Progress within phase (0-1)
 * @returns {Object} Item animation state
 */
const calculateSingleItemState = (config, index, phase, progress) => {
  // Determine slide direction based on icon position
  const isLeftIcon = config.iconPosition === 'left';
  
  const maxSlideDistance = isLeftIcon 
    ? SLIDE_CONFIG.RIGHT_SLIDE_DISTANCE 
    : SLIDE_CONFIG.LEFT_SLIDE_DISTANCE;
  
  // Base hidden state
  const hiddenState = {
    visible: false,
    opacity: 0,
    translateX: maxSlideDistance,
    scale: 1,
    isLeftIcon,
    index
  };
  
  // Calculate based on phase
  switch (phase) {
    case ANIMATION_PHASES.HIDDEN:
      return hiddenState;
    
    case ANIMATION_PHASES.ENTERING: {
      const linearProgress = EASING.linear(progress);
      
      return {
        visible: true,
        opacity: linearProgress,
        translateX: maxSlideDistance * (1 - linearProgress),
        scale: 1,
        isLeftIcon,
        index
      };
    }
    
    case ANIMATION_PHASES.VISIBLE:
      // Fully visible, no movement
      return {
        visible: true,
        opacity: 1,
        translateX: 0,
        scale: 1,
        isLeftIcon,
        index
      };
    
    case ANIMATION_PHASES.EXITING: {
      // LINEAR animation: progress directly controls opacity and position
      // As progress goes 0→1, translateX goes 0→maxDistance, opacity goes 1→0
      const linearProgress = EASING.linear(progress);
      
      return {
        visible: linearProgress < 1,
        opacity: 1 - linearProgress,
        translateX: maxSlideDistance * linearProgress,
        scale: 1,
        isLeftIcon,
        index
      };
    }
    
    default:
      return hiddenState;
  }
};


const calculateSnakeProgress = (phase, progress) => {
  switch (phase) {
    case ANIMATION_PHASES.HIDDEN:
    case ANIMATION_PHASES.ENTERING:
    case ANIMATION_PHASES.EXITING:
      // Snake not visible during these phases
      return 0;
    
    case ANIMATION_PHASES.VISIBLE:
      // Split VISIBLE phase into two equal halves for forward/backward animation
      if (progress <= 0.5) {
        // First 50% of VISIBLE phase: Draw forward (0→1)
        // Map progress range 0-0.5 to snake progress 0-1
        const forwardProgress = progress * 2; // Scale 0-0.5 to 0-1
        return EASING.linear(forwardProgress);
      } else {
        // Second 50% of VISIBLE phase: Retract backward (1→0)
        // Map progress range 0.5-1 to snake progress 1-0
        const backwardProgress = (progress - 0.5) * 2; // Scale 0.5-1 to 0-1
        return EASING.linear(1 - backwardProgress); // Inverse for retraction
      }
    
    default:
      return 0;
  }
};

const createHiddenState = () => ({
  phase: ANIMATION_PHASES.HIDDEN,
  phaseProgress: 0,
  itemStates: SPEC_ITEMS_CONFIG.map((config, index) => ({
    visible: false,
    opacity: 0,
    translateX: config.iconPosition === 'left' 
      ? SLIDE_CONFIG.RIGHT_SLIDE_DISTANCE 
      : SLIDE_CONFIG.LEFT_SLIDE_DISTANCE,
    scale: 1,
    isLeftIcon: config.iconPosition === 'left',
    index
  })),
  snakeProgress: 0,
  isVisible: false,
  isFullyVisible: false,
  isEntering: false,
  isExiting: false,
  boundaries: null,
  currentFrame: 0
});

/**
 * Check if a specific frame is within the active highlight zone
 * 
 * @param {number} frame - Frame to check
 * @param {number} totalFrames - Total frames
 * @returns {boolean} True if in active zone
 */
export const isFrameInHighlightZone = (frame, totalFrames) => {
  const boundaries = calculateFrameBoundaries(totalFrames);
  return frame >= boundaries.entryStart && frame <= boundaries.exitEnd;
};

/**
 * Get debug info for current state (useful for development)
 * 
 * @param {number} currentFrame - Current frame
 * @param {number} totalFrames - Total frames
 * @returns {Object} Debug information
 */
export const getDebugInfo = (currentFrame, totalFrames) => {
  const state = calculateAnimationState(currentFrame, totalFrames);
  const boundaries = calculateFrameBoundaries(totalFrames);
  
  return {
    currentFrame,
    totalFrames,
    ...boundaries,
    phase: state.phase,
    phaseProgress: state.phaseProgress,
    isInZone: isFrameInHighlightZone(currentFrame, totalFrames),
    snakeProgress: state.snakeProgress,
    itemStates: state.itemStates.map(item => ({
      opacity: item.opacity.toFixed(2),
      translateX: item.translateX.toFixed(0),
      isLeftIcon: item.isLeftIcon
    }))
  };
};

// ============================================
// EXPORTS
// ============================================

const frameCalculatorExport = {
  calculateAnimationState,
  calculateFrameBoundaries,
  isFrameInHighlightZone,
  getDebugInfo
};

export default frameCalculatorExport;
